import { Map, Marker, useMap } from '@vis.gl/react-google-maps';
import { useEffect, useRef, useState } from 'react';
import { LOCATION_PLACE_SEARCH_ENABLED } from './GoogleMapsAction.functions.ts';
import {
  tGoogleMapsPickerProps,
  tLatLng,
  tPickedPlace,
} from './GoogleMapsAction.types.ts';
import PlaceSearch from './PlaceSearch.component.tsx';

const WORLD_VIEW = { lat: 22.54992, lng: 0 };
const LOCATED_ZOOM = 15;
const PLACE_ZOOM = 16;

/**
 * Drives the uncontrolled map. `defaultCenter` applies only at mount, and the
 * advisor's location usually arrives after it (geolocation is asynchronous), so
 * the first location centers the map once. A picked place moves it there. A pin
 * the advisor drags does not move the map: they are placing it by hand.
 */
function MapCamera(props: {
  userLocation: tLatLng | null;
  placeTarget: tLatLng | null;
}) {
  const map = useMap();
  const centeredOnUserRef = useRef(false);

  useEffect(() => {
    if (!map || !props.userLocation || centeredOnUserRef.current) return;
    centeredOnUserRef.current = true;
    map.setCenter(props.userLocation);
    map.setZoom(LOCATED_ZOOM);
  }, [map, props.userLocation]);

  useEffect(() => {
    if (!map || !props.placeTarget) return;
    // A pick also counts as being located: a late geolocation must not undo it.
    centeredOnUserRef.current = true;
    map.panTo(props.placeTarget);
    map.setZoom(PLACE_ZOOM);
  }, [map, props.placeTarget]);

  return null;
}

/**
 * INT-1720 — the location picker's map, with a place search above it when the
 * search is enabled. Picking a place moves the pin there; dragging the pin
 * still works as before. Lives inside the APIProvider, which the search's
 * Places library needs.
 */
export default function GoogleMapsPicker(props: tGoogleMapsPickerProps) {
  const [markerOverride, setMarkerOverride] = useState<tLatLng | null>(null);
  const [placeTarget, setPlaceTarget] = useState<tLatLng | null>(null);
  const markerPosition = markerOverride ?? props.userLocation ?? WORLD_VIEW;
  // Counts what last placed the pin: each pick and each drag. A pick whose
  // details arrive after a newer pick or a drag must not move the pin back.
  const selectionRef = useRef(0);

  const handlePickStart = () => {
    selectionRef.current += 1;
    return selectionRef.current;
  };

  const handlePicked = (picked: tPickedPlace, token: number) => {
    if (token !== selectionRef.current) return;
    setMarkerOverride(picked.location);
    setPlaceTarget(picked.location);
    props.onPlacePicked(picked);
  };

  const handleDragEnd = (event: google.maps.MapMouseEvent) => {
    selectionRef.current += 1;
    if (event.latLng) {
      setMarkerOverride({ lat: event.latLng.lat(), lng: event.latLng.lng() });
    }
    props.onMarkerDragEnd(event);
  };

  return (
    <div className="GoogleMapsPicker">
      {LOCATION_PLACE_SEARCH_ENABLED && (
        <PlaceSearch
          userLocation={props.userLocation}
          onPickStart={handlePickStart}
          onPicked={handlePicked}
        />
      )}
      <div className="GoogleMapsPicker__Map">
        <Map
          defaultCenter={props.userLocation ?? WORLD_VIEW}
          defaultZoom={props.userLocation ? LOCATED_ZOOM : 3}
        >
          <Marker
            position={markerPosition}
            draggable={true}
            onDragEnd={handleDragEnd}
          />
        </Map>
        <MapCamera
          userLocation={props.userLocation}
          placeTarget={placeTarget}
        />
      </div>
    </div>
  );
}
